import React, { useState, useRef, useEffect } from 'react';
import { getBillLineItems, resolveBillLineItemName } from '../utils/itemDisplayUtils';

const ActionButtons = ({
  actions = [],
  className = "",
  size = "sm", // sm, md, lg
  enableDropdown = false, // Enable dropdown for view action
  billData = null, // Bill data for dropdown content
  items = [] // Items data for proper name mapping
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Detect mobile screen size
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint is 768px
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  // Update dropdown position on scroll/resize
  useEffect(() => {
    if (showDropdown) {
      const updatePosition = () => {
        const position = calculateDropdownPosition();
        setDropdownPosition(position);
      };

      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showDropdown]);
  const getIcon = (action) => {
    switch (action.type) {
      case 'view': 
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        );
      case 'edit': 
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        );
      case 'delete': 
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        );
      case 'payment': 
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        );
      case 'receipts': 
        return `R(${action.count || 0})`;
      case 'invoice': 
      case 'challan': 
      case 'quotation': 
      case 'purchaseBill': 
      case 'purchaseOrder': 
      case 'purchase': 
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'print': 
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
        );
      case 'pdf': 
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'download': 
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'preview': 
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        );
      case 'back': 
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        );
      case 'close': 
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      case 'convert_to_invoice': 
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
        );
      default: 
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        );
    }
  };

  const getTooltip = (action) => {
    switch (action.type) {
      case 'view': return 'View Details';
      case 'edit': return 'Edit';
      case 'delete': return 'Delete';
      case 'payment': return 'Add Payment';
      case 'receipts':
        if (action.count > 0) {
          return action.receiptNumbers ?
            `Receipts (${action.count}): ${action.receiptNumbers.join(', ')}` :
            `Receipts (${action.count})`;
        }
        return 'Receipts (0)';
      case 'invoice': return 'View Invoice';
      case 'challan': return 'View Challan';
      case 'quotation': return 'View Quotation';
      case 'purchaseBill': return 'View Purchase Bill';
      case 'purchaseOrder': return 'View Purchase Order';
      case 'print': return 'Print';
      case 'pdf': return 'Save as PDF';
      case 'download': return 'Download';
      case 'preview': return 'Preview';
      case 'back': return 'Back to List';
      case 'close': return 'Close';
      case 'convert_to_invoice': return 'Convert to Invoice';
      default: return action.tooltip || action.label || 'Action';
    }
  };

  const getButtonClasses = (action) => {
    const baseClasses = `inline-flex items-center justify-center rounded transition-colors duration-200 hover:scale-110 ${
      size === 'sm' ? 'w-6 h-6 text-xs' :
      size === 'md' ? 'w-8 h-8 text-sm' :
      'w-10 h-10 text-base'
    }`;

    switch (action.type) {
      case 'view': return `${baseClasses} text-blue-600 hover:text-blue-800 hover:bg-blue-50`;
      case 'edit': return `${baseClasses} text-blue-600 hover:text-blue-800 hover:bg-blue-50`;
      case 'delete': return `${baseClasses} text-red-600 hover:text-red-800 hover:bg-red-50`;
      case 'payment': return `${baseClasses} text-green-600 hover:text-green-800 hover:bg-green-50`;
      case 'receipts': return `${baseClasses} text-purple-600 hover:text-purple-800 hover:bg-purple-50`;
      case 'invoice': return `${baseClasses} text-blue-600 hover:text-blue-800 hover:bg-blue-50`;
      case 'challan': return `${baseClasses} text-blue-600 hover:text-blue-800 hover:bg-blue-50`;
      case 'quotation': return `${baseClasses} text-blue-600 hover:text-blue-800 hover:bg-blue-50`;
      case 'purchaseBill': return `${baseClasses} text-blue-600 hover:text-blue-800 hover:bg-blue-50`;
      case 'purchaseOrder': return `${baseClasses} text-blue-600 hover:text-blue-800 hover:bg-blue-50`;
      case 'purchase': return `${baseClasses} text-blue-600 hover:text-blue-800 hover:bg-blue-50`;
      case 'print': return `${baseClasses} text-blue-600 hover:text-blue-800 hover:bg-blue-50`;
      case 'pdf': return `${baseClasses} text-green-600 hover:text-green-800 hover:bg-green-50`;
      case 'download': return `${baseClasses} text-blue-600 hover:text-blue-800 hover:bg-blue-50`;
      case 'preview': return `${baseClasses} text-blue-600 hover:text-blue-800 hover:bg-blue-50`;
      case 'back': return `${baseClasses} text-gray-600 hover:text-gray-800 hover:bg-gray-50`;
      case 'close': return `${baseClasses} text-gray-600 hover:text-gray-800 hover:bg-gray-50`;
      case 'convert_to_invoice': return `${baseClasses} text-green-600 hover:text-green-800 hover:bg-green-50`;
      default: return `${baseClasses} text-gray-600 hover:text-gray-800 hover:bg-gray-50`;
    }
  };

  const calculateDropdownPosition = () => {
    if (!buttonRef.current) {
      console.log('No button ref found');
      return { top: 100, left: 100 }; // Fallback position
    }

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Use responsive width - 320px on mobile, 384px on desktop
    const dropdownWidth = viewportWidth < 640 ? 320 : 384;
    const dropdownHeight = 384; // max-h-96 = 384px
    const gap = 8; // mt-2 = 8px
    const margin = 16; // 16px margin from edges
    
    // Calculate viewport-relative positions (no scroll offset needed for fixed positioning)
    const buttonTop = buttonRect.top;
    const buttonLeft = buttonRect.left;
    const buttonBottom = buttonRect.bottom;
    
    let position = {
      top: buttonBottom + gap,
      left: buttonLeft,
      right: 'auto',
      bottom: 'auto'
    };

    // Check right edge overflow
    if (buttonLeft + dropdownWidth > viewportWidth) {
      position.left = viewportWidth - dropdownWidth - margin;
    }

    // Check left edge overflow
    if (position.left < margin) {
      position.left = margin;
    }

    // Check bottom edge overflow
    if (buttonBottom + gap + dropdownHeight > viewportHeight) {
      // Position above button instead
      const spaceAbove = buttonTop;
      if (spaceAbove >= dropdownHeight + gap) {
        position.top = 'auto';
        position.bottom = viewportHeight - buttonTop + gap;
      } else {
        // If not enough space above either, position at top of viewport
        position.top = margin;
        position.bottom = 'auto';
      }
    }

    return position;
  };

  const handleViewClick = (action) => {
    if (enableDropdown && action.type === 'view') {
      if (!showDropdown) {
        const position = calculateDropdownPosition();
        console.log('Dropdown position calculated:', position);
        console.log('Button rect:', buttonRef.current?.getBoundingClientRect());
        console.log('Viewport:', { width: window.innerWidth, height: window.innerHeight });
        setDropdownPosition(position);
      }
      setShowDropdown(!showDropdown);
    } else {
      action.onClick();
    }
  };

  const renderDropdownContent = () => {
    if (!billData) return null;
    
    // Format date as DD/MM/YYYY
    let date = billData.invoiceDate || billData.date || "";
    if (date && date.includes("-")) {
      const [yyyy, mm, dd] = date.split("-");
      date = `${dd}/${mm}/${yyyy}`;
    }
    
    // Format amount with currency
    const amount = typeof billData.amount === "number"
      ? billData.amount.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })
      : billData.amount;

    // Get items from bill data - try both items and rows
    const billItems = getBillLineItems(billData);
    
    // Get payment details
    const payments = billData.payments || [];
    const totalPaid = billData.paid || 0;
    const outstanding = billData.outstanding || 0;

    return (
      <>
        {/* Mobile backdrop - only show on mobile screens */}
        {isMobile && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-25 z-[9998]"
            onClick={() => setShowDropdown(false)}
          />
        )}
        {/* Dropdown */}
        <div 
          className="fixed w-80 sm:w-96 max-w-[calc(100vw-2rem)] sm:max-w-none bg-white rounded-lg shadow-xl border border-gray-200 z-[9999] max-h-96 overflow-y-auto"
          style={{
            ...dropdownPosition,
            position: 'fixed',
            zIndex: 9999
          }}
        >
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-800">Bill Details</h3>
              <button
                onClick={() => setShowDropdown(false)}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                ×
              </button>
            </div>
            
            {/* Basic Bill Information */}
            <div className="space-y-2 text-sm mb-4">
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Bill No:</span>
                <span className="text-gray-800">{billData.number || billData.invoiceNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Date:</span>
                <span className="text-gray-800">{date}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Party:</span>
                <span className="text-gray-800">{billData.partyName || 'Unknown'}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Total Amount:</span>
                <span className="text-gray-800 font-semibold">{amount}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Paid:</span>
                <span className="text-green-600 font-semibold">{totalPaid.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Outstanding:</span>
                <span className="text-red-600 font-semibold">{outstanding.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</span>
              </div>
            </div>

            {/* Items Section */}
            {billItems.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Items</h4>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {billItems.map((item, index) => {
                    const itemName = resolveBillLineItemName(item, items, 'Unknown Item');
                    const qty = item?.qty || item?.nos || item?.quantity || 0;
                    const rate = item?.rate || item?.price || item?.unitPrice || 0;
                    const amount = item?.amount || item?.total || (qty * rate);
                    
                    return (
                      <div key={index} className="text-xs bg-gray-50 p-2 rounded">
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-700">{itemName}</span>
                          <span className="text-gray-600">{amount.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</span>
                        </div>
                        {(qty > 0 || rate > 0) && (
                          <div className="text-gray-500">
                            Qty: {qty} × Rate: {rate.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Payment Details Section */}
            {payments.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Payment Received</h4>
                <div className="space-y-1 max-h-24 overflow-y-auto">
                  {payments.map((payment, index) => (
                    <div key={index} className="text-xs bg-green-50 p-2 rounded">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-700">Receipt: {payment.receiptNumber || `#${index + 1}`}</span>
                        <span className="text-green-600 font-semibold">
                          {(payment.amount || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                        </span>
                      </div>
                      {payment.date && (
                        <div className="text-gray-500">
                          Date: {payment.date.includes('-') ? 
                            (() => {
                              const [yyyy, mm, dd] = payment.date.split('-');
                              return `${dd}/${mm}/${yyyy}`;
                            })() : payment.date
                          }
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {billData.notes && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-1">Notes</h4>
                <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                  {billData.notes}
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="pt-3 border-t border-gray-200">
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowDropdown(false);
                    // Find edit action and trigger it
                    const editAction = actions.find(a => a.type === 'edit');
                    if (editAction) editAction.onClick();
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs py-2 px-3 rounded-md transition duration-200"
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    setShowDropdown(false);
                    // Find print action and trigger it
                    const printAction = actions.find(a => a.type === 'invoice' || a.type === 'challan' || a.type === 'quotation');
                    if (printAction) printAction.onClick();
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs py-2 px-3 rounded-md transition duration-200"
                >
                  Print
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className={`flex items-center gap-1 relative ${className}`} ref={dropdownRef}>
      {actions.map((action, index) => (
        <div key={index} className="relative">
          <button
            ref={action.type === 'view' ? buttonRef : null}
            onClick={() => handleViewClick(action)}
            className={getButtonClasses(action)}
            title={getTooltip(action)}
            disabled={action.disabled}
          >
            {getIcon(action)}
          </button>
          {enableDropdown && action.type === 'view' && showDropdown && renderDropdownContent()}
        </div>
      ))}
    </div>
  );
};

export default ActionButtons; 